// ──────────────────────────────────────────────────────────────
//  SortCodeReportTable.jsx
// ──────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import editIconImage from '../../../assests/images/Icons/editIcon.svg';
import { beautifyNumber } from '../../../Utils/FormatNumber';

/* -------------------------------------------------------------------------- */
/* component                                                                  */
/* -------------------------------------------------------------------------- */
const SortCodeReportTable = ({
  filteredAndSortedNodes = [],
  columns = [],
  handleEdit,
  footerTotals = {},
  sortConfig = {},
  onSortChange,
}) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();

  /* ─────── pagination ─────── */
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  useEffect(() => setPage(0), [filteredAndSortedNodes]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSortedNodes.slice(start, start + rowsPerPage);
  }, [filteredAndSortedNodes, page, rowsPerPage]);

  /* ─────── helpers ─────── */
  const clean = (v) =>
    v === null || v === undefined || v === 'null' ? '' : v;

  const renderValue = (v) =>
    v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v))
      ? ''
      : v;

  const totalsByField = useMemo(
    () => ({
      't-amount-vat': beautifyNumber(footerTotals['t-amount-vat']),
      't-amount-no-vat': beautifyNumber(footerTotals['t-amount-no-vat']),
      't-vat': beautifyNumber(footerTotals['t-vat']),
    }),
    [footerTotals],
  );

  /* ─────── styles ─────── */
  const TOTAL_ROW_HEIGHT = 32;
  const ICON_COL_WIDTH = 50;

  /* Proportional column widths — same idea as ClientsDataGrid flex + minWidth weights */
  const columnLayout = useMemo(() => {
    const weights = columns.map((c) => c.width ?? 120);
    const totalWeight = weights.reduce((sum, w) => sum + w, ICON_COL_WIDTH);
    return {
      iconPercent: (ICON_COL_WIDTH / totalWeight) * 100,
      columnPercents: weights.map((w) => (w / totalWeight) * 100),
    };
  }, [columns]);

  const expandCellContentStyle = {
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 4,
    wordBreak: 'break-word',
    maxWidth: '100%',
    maxHeight: 'calc(var(--xtram-grid-cell-font, 13px) * 1.3 * 4)',
    width: '100%',
    fontSize: 'var(--xtram-grid-cell-font, 13px)',
    color: '#334155',
    lineHeight: 1.3,
  };

  const headerStyle = {
    backgroundColor: 'var(--xtram-grid-header-bg, #E3E7FE)',
    color: 'var(--xtram-grid-header-color, #1222b3)',
    textAlign: 'center',
    fontFamily: 'Montserrat',
    fontSize: 'var(--xtram-grid-header-font, 13px)',
    fontWeight: 'bold',
    height: 'var(--xtram-grid-row-height, 40px)',
    maxHeight: 'var(--xtram-grid-row-height, 40px)',
    padding: '0 6px',
    cursor: 'pointer',
    boxSizing: 'border-box',
  };

  const bodyCellSx = {
    minHeight: 'var(--xtram-grid-row-height, 40px)',
    padding: '4px 6px',
    py: 0,
    boxSizing: 'border-box',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    verticalAlign: 'middle',
    color: '#334155',
    fontSize: 'var(--xtram-grid-cell-font, 13px)',
    borderInlineEnd: '1px solid #F1F5F9',
    borderBottom: '1px solid #EEF1F5',
  };

  const totalCellSx = {
    ...bodyCellSx,
    minHeight: TOTAL_ROW_HEIGHT,
    height: TOTAL_ROW_HEIGHT,
    padding: '10px 6px',
    fontWeight: 'bold',
    backgroundColor: '#f5f7ff',
  };

  const totalCellContentStyle = {
    ...expandCellContentStyle,
    maxHeight: 'none',
    WebkitLineClamp: 'unset',
    display: 'block',
    fontWeight: 'bold',
    fontSize: 'var(--xtram-grid-cell-font, 13px)',
  };

  const isLongText = (field) => field === 't-details';

  /* ─────── sorting handlers ─────── */
  const handleSort = (field) => {
    if (!onSortChange) return;
    
    const isNumeric = /(amount|vat)$/i.test(field);
    const isDate = /date$/i.test(field);
    
    let newDirection = 'ascending';
    if (sortConfig.key === field && sortConfig.direction === 'ascending') {
      newDirection = 'descending';
    }
    
    const type = isNumeric ? 'number' : isDate ? 'date' : 'string';
    onSortChange(field, type, newDirection);
  };

  const getSortIcon = (field) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'ascending' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  };

  const renderCellContent = (field, rawValue) => {
    const value = renderValue(clean(rawValue));
    if (!value) return '';

    if (isLongText(field)) {
      return (
        <Tooltip title={value} arrow placement="top-start">
          <span style={expandCellContentStyle}>{value}</span>
        </Tooltip>
      );
    }

    return <span style={expandCellContentStyle}>{value}</span>;
  };

  /* ─────── render ─────── */
  return (
    <div className={`sort-code-report-table ${direction}`} style={{ width: '100%', height: '65vh' }}>
      <TableContainer
        component={Paper}
        sx={{ height: '65vh', overflow: 'auto' }}
      >
        <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: `${columnLayout.iconPercent}%` }} />
            {columns.map((col, idx) => (
              <col key={col.field} style={{ width: `${columnLayout.columnPercents[idx]}%` }} />
            ))}
          </colgroup>

          {/* ---- HEADER ---- */}
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ ...headerStyle, minWidth: ICON_COL_WIDTH, cursor: 'default' }}
                align="center"
              />
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  align="center"
                  className="table-headers"
                  sx={{ ...headerStyle, minWidth: col.width ?? 120 }}
                  onClick={() => handleSort(col.field)}
                >
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
                    {col.header}
                    {getSortIcon(col.field)}
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* ---- BODY ---- */}
          <TableBody>
            {pagedRows.map((node) => (
              <TableRow
                key={node.key}
                sx={{ minHeight: 'var(--xtram-grid-row-height, 40px)', borderBottom: '1px solid #EEF1F5' }}
                style={{ transition: 'transform 0.2s ease-in-out' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                {/* edit icon */}
                <TableCell align="center" sx={bodyCellSx}>
                  <IconButton size="small" onClick={() => handleEdit(node)} sx={{ p: 0.5 }}>
                    <img src={editIconImage} alt="edit" />
                  </IconButton>
                </TableCell>

                {/* data cells */}
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    align="center"
                    sx={bodyCellSx}
                  >
                    {renderCellContent(col.field, node.data[col.field])}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {/* ---- TOTAL ROW ---- */}
            <TableRow
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 1,
                minHeight: TOTAL_ROW_HEIGHT,
                height: TOTAL_ROW_HEIGHT,
                backgroundColor: '#f5f7ff',
              }}
            >
              <TableCell align="center" sx={totalCellSx} />
              {columns.map((col, idx) => (
                <TableCell
                  key={col.field}
                  align="center"
                  sx={totalCellSx}
                >
                  {idx === 0 ? (
                    <span style={totalCellContentStyle}>{t('Total')}</span>
                  ) : (
                    <span style={totalCellContentStyle}>{totalsByField[col.field] ?? ''}</span>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* ---- PAGINATION ---- */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={filteredAndSortedNodes.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage={t('Rows per page')}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${t('of')} ${count !== -1 ? count : `${t('more than')} ${to}`
          }`
        }
      />
    </div>
  );
};

export default SortCodeReportTable;
